import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { apiClient, setAuthToken, setLastActivity } from '../utils/apiUtils';
import useInactivity from '../hooks/useInactivity';

// Configure base URL from environment for production builds
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production'
  ? 'https://extensync.onrender.com/api'
  : 'http://localhost:5000/api');

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    const storedToken = localStorage.getItem('token');
    console.log('AuthContext initialization - token from localStorage:', storedToken ? 'present' : 'missing');
    return storedToken;
  });
  const [lastActivity, setLastActivity] = useState(() => {
    return localStorage.getItem('lastActivity') ? parseInt(localStorage.getItem('lastActivity')) : Date.now();
  });

  // Set up axios defaults
  axios.defaults.baseURL = API_BASE_URL;
  
  // Set initial token on apiClient if it exists
  if (token) {
    console.log('AuthContext initialization - setting initial token on apiClient');
    setAuthToken(token);
  } else {
    console.log('AuthContext initialization - no initial token to set');
  }

  // Add token to requests if it exists
  useEffect(() => {
    console.log('AuthContext useEffect - token changed:', token ? 'present' : 'missing');
    console.log('AuthContext useEffect - token value:', token);
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setAuthToken(token); // Also set token on apiClient
      localStorage.setItem('token', token);
      console.log('Token set in axios and apiClient');
      console.log('localStorage after setting:', localStorage.getItem('token'));
    } else {
      delete axios.defaults.headers.common['Authorization'];
      setAuthToken(null); // Clear token from apiClient
      localStorage.removeItem('token');
      console.log('Token cleared from axios and apiClient');
    }
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          console.log('Checking auth with token:', token ? 'present' : 'missing');
          const response = await apiClient.get('/auth/me');
          setUser(response.data);
        } catch (error) {
          console.error('Auth check failed:', error);
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (email, password, loginType = 'employee') => {
    try {
      const endpoint = loginType === 'beneficiary' ? '/auth/beneficiary-login' : '/auth/login';
      console.log('Login attempt:', { endpoint, email, loginType });
      const response = await axios.post(endpoint, { email, password });
      console.log('Login response:', response.data);
      const { token: newToken } = response.data;
      
      // Set token first so subsequent requests include it
      console.log('Login successful, setting token:', newToken ? 'present' : 'missing');
      console.log('Token value:', newToken);
      
      // Set token on apiClient immediately before making the /auth/me request
      setAuthToken(newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      localStorage.setItem('token', newToken);
      
      console.log('Token set immediately on apiClient and axios');
      console.log('apiClient headers:', apiClient.defaults.headers.common);
      
      // Set token in state (this will trigger useEffect)
      setToken(newToken);
      
      // Immediately fetch the current user using the new token
      console.log('Fetching user data with new token...');
      const meResponse = await apiClient.get('/auth/me');
      const userData = meResponse.data;
      console.log('User data fetched:', userData);
      setUser(userData);
      
      // Return success only after user data is fully loaded
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      // Clear any partial state on error
      setToken(null);
      setUser(null);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Invalid e-mail or password.' 
      };
    }
  };


  const logout = async () => {
    try {
      // Call the backend logout endpoint to log the logout action
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Clear local state regardless of API call success
      setToken(null);
      setUser(null);
      setLoading(false);
    }
  };

  const logoutWithRedirect = async (navigate) => {
    const currentUser = user;
    
    try {
      // Call the backend logout endpoint to log the logout action
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Clear local state regardless of API call success
      setToken(null);
      setUser(null);
      setLoading(false);
      
      // Small delay to ensure state is cleared before redirect
      setTimeout(() => {
        // All users go to employee login
        navigate('/login');
      }, 100);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  // Handle inactivity logout
  const handleInactivity = async () => {
    console.log('User inactive for 2 hours, logging out...');
    try {
      await logout();
      // Show notification to user
      if (window.confirm('You have been inactive for 2 hours and have been automatically logged out for security reasons. Click OK to log in again.')) {
        window.location.href = '/login';
      } else {
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Error during inactivity logout:', error);
      // Force redirect even if logout fails
      window.location.href = '/login';
    }
  };

  // Use inactivity hook (2 hours = 7200000ms)
  useInactivity(7200000, handleInactivity);

  // Update last activity timestamp on user actions
  useEffect(() => {
    const updateLastActivity = () => {
      const now = Date.now();
      setLastActivity(now);
      setLastActivity(now); // Set in apiClient headers
      localStorage.setItem('lastActivity', now.toString());
    };

    // Events that indicate user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown'];
    
    events.forEach(event => {
      document.addEventListener(event, updateLastActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateLastActivity, true);
      });
    };
  }, []);

  // Set initial last activity timestamp
  useEffect(() => {
    if (lastActivity) {
      setLastActivity(lastActivity);
    }
  }, [lastActivity]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    logoutWithRedirect,
    updateUser,
    lastActivity
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
